"use client";
import company_logo from "../../../../../../src/images/company_logo.jpg";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

// Styles
const styles = StyleSheet.create({
  page: {
    fontFamily: "Times-Roman",
    fontSize: 9,
    padding: 20,
    backgroundColor: "#fff",
    lineHeight: 1.4,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 6,
  },
  companyName: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 9,
    color: "#000",
  },
  logoWrapper: {
    position: "relative",
    width: 50,
    height: 50,
  },
  logo: {
    width: 40,
    height: 40,
  },
  trademark: {
    position: "absolute",
    right: 5,
    top: -2,
    fontSize: 12,
    fontWeight: "bold",
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginVertical: 6,
    textAlign: "center",
    textDecoration: "underline",
  },
  table: {
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 10,
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
  },
  cell: {
    flex: 1,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#000",
  },
  cellRight: {
    flex: 1,
    padding: 4,
    textAlign: "right",
  },
  cellLast: {
    flex: 1,
    padding: 4,
  },
  bold: {
    fontWeight: "bold",
  },
  center: {
    textAlign: "center",
  },
  footer: {
    fontSize: 8,
    marginTop: 12,
    textAlign: "center",
    color: "#000",
  },
  netPayRow: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderTopWidth: 1,
    borderColor: "#000",
  },
  netPayCell: {
    flex: 1,
    padding: 4,
    fontWeight: "bold",
    borderRightWidth: 1,
    borderRightColor: "#000",
  },
  netPayValue: {
    flex: 1,
    padding: 4,
    fontWeight: "bold",
    textAlign: "right",
    fontSize: 11,
  },
});

const logo = company_logo;

const n = (val: string | number | null | undefined): number =>
  parseFloat(String(val ?? 0).replace(/[^0-9.-]/g, "")) || 0;

// Format currency with Rs.
const formatCurrency = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return "Rs. 0";
  }
  return (
    "Rs. " +
    new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(Number(value))
  );
};

// PDF Layout
function SalarySlipPDF({ payroll }: { payroll: any }) {

  const totalEarnings =
    n(payroll.basic) +
    n(payroll.hra) +
    n(payroll.special_allowance) +
    n(payroll.misc_allowance) +
    n(payroll.statutory_bonus) +
    n(payroll.incentives) +
    n(payroll.otherEarnings) +
    n(payroll.travel_allowances);

  const totalDeductions =
    n(payroll.pf) +
    n(payroll.esic) +
    n(payroll.incomeTaxDeduction) +
    n(payroll.health_deduction) +
    n(payroll.other_deduction) +
    n(payroll.lop_deduction) +
    n(payroll.ap_tax);

  const totalReimbursements =
    n(payroll.reimbursementTravel) + n(payroll.reimbursementOthers);

  const netPay = totalEarnings - totalDeductions + totalReimbursements;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerRow}>
          <View>
            <Text style={styles.companyName}>
              Optim Health and Wellness (OPC) Pvt. Ltd.
            </Text>
            <Text style={styles.companyDetails}>
              H. No. 01, Karbi Namghar Path, Khagen Mahanta Path, Hengrabari,
              Barbari, Guwahati, Dispur, Assam, India.
            </Text>
            <Text style={[styles.companyDetails, { marginTop: 6 }]}></Text>
          </View>

          <View style={styles.logoWrapper}>
            {logo && <Image src={logo} style={styles.logo} />}
            <Text style={styles.trademark}>®</Text>
          </View>
        </View>

        {/* Employee Pay Summary */}
        <Text style={styles.sectionTitle}>
          Payslip for the month of {payroll.month}, {payroll.year}
        </Text>
        <View style={styles.table}>
          <View style={styles.row}>
            <Text style={[styles.cellLast, styles.bold, styles.center]}>
              EMPLOYEE PAY SUMMARY
            </Text>
          </View>

          {[
            ["Employee Name", payroll.name, "PAN", payroll.pan],
            [
              "Designation",
              payroll.designation,
              "Bank Name",
              payroll.bank_name,
            ],
            [
              "Employee ID",
              payroll.employee_code,
              "Bank A/C No.",
              payroll.ac_number,
            ],
            [
              "Date of Joining",
              new Date(payroll.date_of_joining).toLocaleDateString() || "N/A",
              "P.F. A/C No",
              payroll.pf_account_number || "N/A",
            ],
            [
              "Department",
              payroll.department,
              "UAN Number",
              payroll.uan_number || "N/A",
            ],
            [
              "Pay Date",
              new Date(payroll.pay_date).toLocaleDateString() || "N/A",
              "ESIC UAN", payroll.esic_uan || "N/A"
            ],
          ].map((row, idx) => (
            <View style={styles.row} key={idx}>
              <Text style={styles.cell}>{row[0]}</Text>
              <Text style={styles.cell}>{row[1]}</Text>
              <Text style={styles.cell}>{row[2]}</Text>
              <Text style={styles.cellLast}>{row[3]}</Text>
            </View>
          ))}
        </View>

        {/* Earnings & Deductions */}
        <View style={{ flexDirection: "row", marginBottom: 12 }}>
          {/* Earnings */}
          <View style={[styles.table, { flex: 1, marginRight: 5 }]}>
            <View style={styles.row}>
              <Text style={[styles.cellLast, styles.bold, styles.center]}>
                EARNINGS
              </Text>
            </View>
            {[
              ["Basic", payroll.basic],
              ["House Rent Allowance", payroll.hra],
              ["Special Allowance", payroll.special_allowance],
              ["Travel Allowance", payroll.travel_allowances],
              ["Misc Allowance", payroll.misc_allowance],
              ["Statutory Bonus", payroll.statutory_bonus],
              ["Incentives", payroll.incentives],
              ["Other", payroll.otherEarnings],
            ].map((row, idx) => (
              <View style={styles.row} key={idx}>
                <Text style={styles.cell}>{row[0]}</Text>
                <Text style={styles.cellRight}>{formatCurrency(row[1])}</Text>
              </View>
            ))}
            <View style={styles.row}>
              <Text style={[styles.cell, styles.bold]}>TOTAL EARNINGS</Text>
              <Text style={[styles.cellRight, styles.bold]}>
                {formatCurrency(totalEarnings)}
              </Text>
            </View>
          </View>

          {/* Deductions */}
          <View style={[styles.table, { flex: 1, marginLeft: 5 }]}>
            <View style={styles.row}>
              <Text style={[styles.cellLast, styles.bold, styles.center]}>
                DEDUCTIONS
              </Text>
            </View>
            {[
              ["Income Tax Deduction", payroll.incomeTaxDeduction],
              ["ESIC", payroll.esic],
              ["P.F.", payroll.pf],
              ["Healthcare Deduction", payroll.health_deduction],
              ["Other Deduction", payroll.other_deduction],
              ["LOP Deduction", payroll.lop_deduction],
              ["AP TAX", payroll.ap_tax],
            ].map((row, idx) => (
              <View style={styles.row} key={idx}>
                <Text style={styles.cell}>{row[0]}</Text>
                <Text style={styles.cellRight}>{formatCurrency(row[1])}</Text>
              </View>
            ))}
            <View style={styles.row}>
              <Text style={[styles.cell, styles.bold]}>TOTAL DEDUCTIONS</Text>
              <Text style={[styles.cellRight, styles.bold]}>
                {formatCurrency(totalDeductions)}
              </Text>
            </View>
          </View>
        </View>

        {/* Reimbursements */}
        <Text style={styles.sectionTitle}>REIMBURSEMENTS</Text>
        <View style={styles.table}>
          {[
            ["Reimbursement (Travel)", payroll.reimbursementTravel],
            ["Reimbursement (Others)", payroll.reimbursementOthers],
          ].map((row, idx) => (
            <View style={styles.row} key={idx}>
              <Text style={styles.cell}>{row[0]}</Text>
              <Text style={styles.cellRight}>{formatCurrency(row[1])}</Text>
            </View>
          ))}
        </View>

        {/* Net Pay Summary */}
        <Text style={styles.sectionTitle}>NET PAY SUMMARY</Text>
        <View style={styles.table}>
          {[
            ["Total Earnings", totalEarnings],
            ["Total Deductions", totalDeductions],
            ["Total Reimbursements", totalReimbursements],
          ].map((row, idx) => (
            <View style={styles.row} key={idx}>
              <Text style={styles.cell}>{row[0]}</Text>
              <Text style={styles.cellRight}>{formatCurrency(row[1])}</Text>
            </View>
          ))}

          {/* Highlighted Net Pay row with border */}
          <View style={styles.netPayRow}>
            <Text style={styles.netPayCell}>TOTAL NET PAYABLE</Text>
            <Text style={styles.netPayValue}>{formatCurrency(netPay)}</Text>
          </View>
        </View>

        {/* Total Net Payable in words */}
        <Text style={[styles.bold, { marginTop: 2, textAlign: "center" }]}>
          Total Net Payable: {payroll.totalNetPayableWords}
        </Text>

        {/* Footer */}
        <Text style={styles.footer}>
          * Total Net Payable = Total Earnings - Total Deductions + Total
          Reimbursements {"\n"}
          L.O.P Days: {payroll.lop ?? 0}
        </Text>
      </Page>
    </Document>
  );
}

// Wrapper with download button
export default function SalarySlipClient({ payroll }: { payroll: any }) {
  return (
    <div className="flex justify-center mt-6">
      <PDFDownloadLink
        document={<SalarySlipPDF payroll={payroll} />}
        fileName={`${payroll.name}_SalarySlip_${payroll.month}_${payroll.year}.pdf`}
      >
        {({ loading }) => (
          <button className="px-4 py-2 bg-[#114b85] text-white rounded-lg shadow hover:bg-[#0d3a66] transition">
            {loading ? "Preparing..." : "Download Salary Slip"}
          </button>
        )}
      </PDFDownloadLink>
    </div>
  );
}
